import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode/ui/dialog"
import { Button } from "@opencode/ui/button"
import { TextInput } from "@opencode/ui/text-input"
import { Checkbox } from "@opencode/ui/checkbox"
import { useDialog } from "@opencode/ui/context/dialog"
import { createEffect, createUniqueId, onCleanup, onMount, Show } from "solid-js"
import { createStore, reconcile } from "solid-js/store"
import type { RemoteWorkspace } from "@opencode/schema/remote-workspace"
import { usePlatform } from "@/runtime/platform/platform"
import { useLanguage } from "@/runtime/i18n/language"
import { useRemoteWorkspace } from "./remote"
import { formatServerError } from "./errors"

export function WorkspaceDetailsDialog(props: { connectionKey: string }) {
  const remote = useRemoteWorkspace()
  const language = useLanguage()
  const dialog = useDialog()
  const formID = createUniqueId()
  const platform = usePlatform()
  const [state, setState] = createStore({
    details: undefined as RemoteWorkspace.Details | undefined,
    cpu: 2,
    memory: 4,
    disk: 8,
    confirm: false,
    busy: false,
    keyBusy: false,
    error: "",
  })
  let closed = false
  let refreshing = false
  const refresh = async (initial = false) => {
    if (refreshing) return
    refreshing = true
    try {
      const value = await remote.details(props.connectionKey)
      if (closed) return
      setState("details", reconcile(value))
      if (initial) {
        const resources = value.pending_resources ?? value.resources
        if (resources)
          setState({ cpu: resources.cpu, memory: resources.memory_mib / 1024, disk: resources.disk_mib / 1024 })
      }
    } catch (error) {
      if (!closed) setState("error", formatServerError(error, language.t))
    } finally {
      refreshing = false
    }
  }
  onMount(() => void refresh(true))
  const timer = setInterval(() => {
    if (!state.busy) void refresh()
  }, 5000)
  onCleanup(() => {
    closed = true
    clearInterval(timer)
  })
  createEffect(() => {
    if (!remote.available() || remote.state.connection?.key !== props.connectionKey) dialog.close()
  })
  const apply = async () => {
    const current = state.details
    if (!current?.id || !state.confirm || state.busy) return
    setState({ busy: true, error: "" })
    try {
      await remote.resize(props.connectionKey, {
        workspace_id: current.id,
        restart: true,
        resources: current.pending_resources ?? {
          cpu: state.cpu,
          memory_mib: Math.round(state.memory * 1024),
          disk_mib: Math.round(state.disk * 1024),
        },
      })
      setState("confirm", false)
      await refresh()
    } catch (error) {
      setState("error", formatServerError(error, language.t))
    } finally {
      setState("busy", false)
    }
  }
  const downloadKey = async () => {
    if (state.keyBusy) return
    setState({ keyBusy: true, error: "" })
    try {
      const key = await remote.sshKey(props.connectionKey)
      if (platform.saveFile) {
        await platform.saveFile(
          { title: language.t("remote.ssh.download"), defaultPath: key.filename, private: true },
          key.privateKey,
        )
        return
      }
      const url = URL.createObjectURL(new Blob([key.privateKey], { type: "application/octet-stream" }))
      const link = document.createElement("a")
      link.href = url
      link.download = key.filename
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (error) {
      if (!closed) setState("error", formatServerError(error, language.t))
    } finally {
      if (!closed) setState("keyBusy", false)
    }
  }
  const copySSH = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command)
    } catch (error) {
      if (!closed) setState("error", formatServerError(error, language.t))
    }
  }
  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("remote.details.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div class="flex min-h-0 flex-col gap-4 overflow-y-auto px-6 pb-6 text-13-regular">
          <Show
            when={state.details}
            fallback={
              <div role="status" aria-label={language.t("common.loading")} class="space-y-3">
                <div class="h-4 w-full rounded bg-v2-background-bg-layer-02 animate-pulse" />
                <div class="h-4 w-2/3 rounded bg-v2-background-bg-layer-02 animate-pulse" />
              </div>
            }
          >
            {(space) => (
              <>
                <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 break-all">
                  <dt>{language.t("remote.details.state")}</dt>
                  <dd>{space().state}</dd>
                  <dt>{language.t("remote.details.id")}</dt>
                  <dd>{space().id || "—"}</dd>
                  <dt>{language.t("remote.details.image")}</dt>
                  <dd>{space().details.image}</dd>
                  <dt>{language.t("remote.details.build")}</dt>
                  <dd>{space().details.build_id}</dd>
                  <dt>{language.t("remote.details.directory")}</dt>
                  <dd>{space().details.directory}</dd>
                  <dt>{language.t("remote.details.selected")}</dt>
                  <dd>
                    {space().resources
                      ? language.t("remote.details.size", {
                          cpu: space().resources!.cpu,
                          memory: space().resources!.memory_mib / 1024,
                          disk: space().resources!.disk_mib / 1024,
                        })
                      : "—"}
                  </dd>
                  <dt>{language.t("remote.details.observed")}</dt>
                  <dd>
                    {space().details.observed_resources
                      ? language.t("remote.details.size", {
                          cpu: space().details.observed_resources!.cpu,
                          memory: space().details.observed_resources!.memory_mib / 1024,
                          disk: space().details.observed_resources!.disk_mib / 1024,
                        })
                      : "—"}
                  </dd>
                  <dt>{language.t("remote.details.limit")}</dt>
                  <dd>
                    {language.t("remote.details.size", {
                      cpu: space().details.limits.cpu,
                      memory: space().details.limits.memory_mib / 1024,
                      disk: space().details.limits.disk_mib / 1024,
                    })}
                  </dd>
                  <dt>{language.t("remote.details.reservedDisk")}</dt>
                  <dd>{language.t("remote.details.mib", { value: space().details.reserved_disk_mib ?? 0 })}</dd>
                  <dt>{language.t("remote.details.idle")}</dt>
                  <dd>{language.t("remote.details.minutes", { value: space().details.idle_seconds / 60 })}</dd>
                  <dt>{language.t("remote.details.background")}</dt>
                  <dd>{language.t("remote.details.minutes", { value: space().details.background_seconds / 60 })}</dd>
                  <Show when={space().details.session_expires_at}>
                    <dt>{language.t("remote.details.sessionExpiry")}</dt>
                    <dd>{new Date(space().details.session_expires_at! * 1000).toLocaleString()}</dd>
                  </Show>
                  <Show when={remote.state.connection}>
                    <dt>{language.t("remote.details.leaseExpiry")}</dt>
                    <dd>{new Date(remote.state.connection!.expiresAt).toLocaleString()}</dd>
                  </Show>
                </dl>
                <Show when={space().details.ssh}>
                  {(ssh) => (
                    <details class="rounded border p-3">
                      <summary>{language.t("remote.ssh.title")}</summary>
                      <div class="mt-3 flex flex-col gap-3">
                        <p>{language.t("remote.ssh.warning")}</p>
                        <p>{language.t("remote.ssh.state", { state: ssh().state })}</p>
                        <Show when={ssh().reason}>
                          <p>{ssh().reason}</p>
                        </Show>
                        <Show when={ssh().command}>
                          <code class="break-all">{ssh().command}</code>
                        </Show>
                        <Show when={ssh().initial_key_fingerprint}>
                          <p class="break-all">
                            {language.t("remote.ssh.fingerprint", { fingerprint: ssh().initial_key_fingerprint! })}
                          </p>
                        </Show>
                        <p>{language.t("remote.ssh.keyNote")}</p>
                        <div class="flex gap-2">
                          <Button
                            disabled={state.keyBusy || !ssh().initial_key_download}
                            onClick={() => void downloadKey()}
                          >
                            {language.t("remote.ssh.download")}
                          </Button>
                          <Button disabled={!ssh().command} onClick={() => void copySSH(ssh().command!)}>
                            {language.t("remote.ssh.copy")}
                          </Button>
                        </div>
                      </div>
                    </details>
                  )}
                </Show>
                <p class="text-xs text-text-weak">{language.t("remote.details.capacityNote")}</p>
                <Show when={space().resource_error || space().error}>
                  <p role="alert">{space().resource_error || space().error}</p>
                </Show>
                <Show
                  when={space().details.resizable && space().resources}
                  fallback={<p>{language.t("remote.details.unavailable")}</p>}
                >
                  <form
                    id={formID}
                    class="flex flex-col gap-3"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void apply()
                    }}
                  >
                    <div class="grid grid-cols-3 gap-2">
                      <label class="flex flex-col gap-2">
                        {language.t("remote.details.cpu")}
                        <TextInput
                          appearance="large"
                          numeric
                          type="number"
                          min="1"
                          step="1"
                          max={Math.max(space().details.limits.cpu, space().resources!.cpu)}
                          value={state.cpu}
                          disabled={!!space().pending_resources}
                          onInput={(e) => setState("cpu", e.currentTarget.valueAsNumber)}
                          required
                        />
                      </label>
                      <label class="flex flex-col gap-2">
                        {language.t("remote.details.memory")}
                        <TextInput
                          appearance="large"
                          numeric
                          type="number"
                          min="0.25"
                          step="0.25"
                          max={Math.max(space().details.limits.memory_mib, space().resources!.memory_mib) / 1024}
                          value={state.memory}
                          disabled={!!space().pending_resources}
                          onInput={(e) => setState("memory", e.currentTarget.valueAsNumber)}
                          required
                        />
                      </label>
                      <label class="flex flex-col gap-2">
                        {language.t("remote.details.disk")}
                        <TextInput
                          appearance="large"
                          numeric
                          type="number"
                          min={space().resources!.disk_mib / 1024}
                          step="1"
                          max={Math.max(space().details.limits.disk_mib, space().resources!.disk_mib) / 1024}
                          value={state.disk}
                          disabled={!!space().pending_resources}
                          onInput={(e) => setState("disk", e.currentTarget.valueAsNumber)}
                          required
                        />
                      </label>
                    </div>
                    <p>{language.t("remote.details.restartNote")}</p>
                    <Checkbox checked={state.confirm} onChange={(checked) => setState("confirm", checked)}>
                      {language.t("remote.details.confirm")}
                    </Checkbox>
                  </form>
                </Show>
              </>
            )}
          </Show>
          <Show when={state.error}>
            <p role="alert">{state.error}</p>
          </Show>
        </div>
      </DialogBody>
      <Show when={state.details?.details.resizable && state.details.resources}>
        <DialogFooter>
          <Button
            type="submit"
            form={formID}
            variant="contrast"
            disabled={
              state.busy || !state.confirm || (!!state.details?.pending_resources && !state.details.resource_error)
            }
          >
            {state.details?.pending_resources ? language.t("remote.details.retry") : language.t("remote.details.apply")}
          </Button>
        </DialogFooter>
      </Show>
    </Dialog>
  )
}
